import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import styles from "./style.module.scss";
import { IdsContext } from "../IdSwitch/IdsContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

interface TyreLapTotal {
  id: string;
  laps: number;
}

interface TyreLapChartProps {
  lapTotals: TyreLapTotal[];
  driverName: string;
  driverCarNum: string;
  driverColor?: string;
  onChartClick?: () => void;
  trackLengthKm?: number | "";
  carStatus?: string;
  currentTyreCodes?: {
    frontLeft?: number;
    frontRight?: number;
    rearLeft?: number;
    rearRight?: number;
  } | null;
  highlightTyreIds?: string[]; // IDs to highlight (most recent TPMS IDs)
}

const TyreLapChart: React.FC<TyreLapChartProps> = ({
  lapTotals,
  driverName,
  driverCarNum,
  driverColor,
  onChartClick,
  trackLengthKm,
  highlightTyreIds = [],
}) => {
  const idsContext = React.useContext(IdsContext);

  const baseColor = driverColor || "rgb(54, 162, 235)";
  const backgroundColor = baseColor
    .replace("rgb(", "rgba(")
    .replace(")", ", 0.6)");
  const borderColor = baseColor;

  const sortedLapTotals = React.useMemo(() => {
    if (!idsContext) return lapTotals;

    const { showCodes, getCodeForDriverAndId } = idsContext;

    return [...lapTotals].sort((a, b) => {
      const codeA = showCodes
        ? getCodeForDriverAndId(driverCarNum, a.id)
        : a.id;
      const codeB = showCodes
        ? getCodeForDriverAndId(driverCarNum, b.id)
        : b.id;

      if (showCodes) {
        const matchA = codeA.match(/^([FR])(\d+)$/);
        const matchB = codeB.match(/^([FR])(\d+)$/);

        if (matchA && matchB) {
          const [, prefixA, numA] = matchA;
          const [, prefixB, numB] = matchB;

          if (prefixA !== prefixB) {
            return prefixA === "F" ? -1 : 1;
          }

          return parseInt(numA, 10) - parseInt(numB, 10);
        }
      }

      return codeA.localeCompare(codeB);
    });
  }, [lapTotals, idsContext, driverCarNum]);

  const chartLabels = sortedLapTotals.map((total) => {
    if (idsContext) {
      const { showCodes, getCodeForDriverAndId } = idsContext;
      return showCodes
        ? getCodeForDriverAndId(driverCarNum, total.id)
        : total.id;
    }
    return total.id;
  });

  // For highlighting bars, set borderWidth and borderColor per bar
  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Laps",
        data: sortedLapTotals.map((total) => total.laps),
        backgroundColor: backgroundColor,
        borderColor: sortedLapTotals.map((total) =>
          highlightTyreIds.includes(total.id) ? "#000" : borderColor,
        ),
        borderWidth: sortedLapTotals.map((total) =>
          highlightTyreIds.includes(total.id) ? 4 : 1,
        ),
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "Tyre Lap Totals",
        font: {
          size: 14,
          weight: "bold" as const,
        },
        color: "#fff",
      },
      tooltip: {
        callbacks: {
          label: function (context: { parsed: { y: number } }) {
            return `${context.parsed.y} laps`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: "#fff",
        },
        title: {
          display: true,
          text: "Number of Laps",
          color: "#fff",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
      x: {
        ticks: {
          color: "#fff",
        },
        title: {
          display: true,
          text: "Tyre ID",
          color: "#fff",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
  };

  if (lapTotals.length === 0) {
    return (
      <div className={styles.noDataMessage}>
        No tyre data available for {driverName}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 8,
          marginTop: 0,
        }}
      >
        <h3 style={{ margin: 0, color: "#fff" }}>
          {driverName} - {driverCarNum}
        </h3>
      </div>
      <div
        className={styles.chartContainer}
        onClick={onChartClick}
        style={{ cursor: onChartClick ? "pointer" : "default" }}
      >
        <Bar data={chartData} options={options} />
      </div>
      <div className={styles.tableContainer}>
        <table className={styles.tyreLapTable}>
          <thead>
            <tr>
              <th className={styles.tableHeader}></th>
              {sortedLapTotals.map((total, index) => {
                const displayId = chartLabels[index] || total.id;
                // Highlight header if this ID is highlighted
                const isHighlighted = highlightTyreIds.includes(total.id);
                let highlightStyle: React.CSSProperties | undefined = undefined;
                if (isHighlighted) {
                  highlightStyle = {
                    ["--highlight-color" as string]: baseColor,
                  } as React.CSSProperties;
                }
                return (
                  <th
                    key={total.id}
                    className={
                      isHighlighted
                        ? `${styles.tableHeader} ${styles.highlighted}`
                        : styles.tableHeader
                    }
                    style={highlightStyle}
                  >
                    {displayId}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {idsContext?.showCodes && (
              <tr>
                <td className={styles.tableLabel}>Tyre ID</td>
                {sortedLapTotals.map((total) => {
                  const isHighlighted = highlightTyreIds.includes(total.id);
                  let highlightStyle: React.CSSProperties | undefined =
                    undefined;
                  if (isHighlighted) {
                    highlightStyle = {
                      ["--highlight-color" as string]: baseColor,
                    } as React.CSSProperties;
                  }
                  return (
                    <td
                      key={total.id + "-orig"}
                      className={
                        isHighlighted
                          ? `${styles.tableCell} ${styles.highlighted}`
                          : styles.tableCell
                      }
                      style={highlightStyle}
                    >
                      {total.id}
                    </td>
                  );
                })}
              </tr>
            )}
            <tr>
              <td className={styles.tableLabel}>Laps</td>
              {sortedLapTotals.map((total) => {
                const isHighlighted = highlightTyreIds.includes(total.id);
                let highlightStyle: React.CSSProperties | undefined = undefined;
                if (isHighlighted) {
                  highlightStyle = {
                    ["--highlight-color" as string]: baseColor,
                  } as React.CSSProperties;
                }
                return (
                  <td
                    key={total.id}
                    className={
                      isHighlighted
                        ? `${styles.tableCell} ${styles.highlighted}`
                        : styles.tableCell
                    }
                    style={highlightStyle}
                  >
                    {total.laps}
                  </td>
                );
              })}
            </tr>
            {typeof trackLengthKm === "number" && trackLengthKm > 0 && (
              <tr>
                <td className={styles.tableLabel}>Kilometreage</td>
                {sortedLapTotals.map((total) => {
                  const isHighlighted = highlightTyreIds.includes(total.id);
                  let highlightStyle: React.CSSProperties | undefined =
                    undefined;
                  if (isHighlighted) {
                    highlightStyle = {
                      ["--highlight-color" as string]: baseColor,
                    } as React.CSSProperties;
                  }
                  return (
                    <td
                      key={total.id + "-mileage"}
                      className={
                        isHighlighted
                          ? `${styles.tableCell} ${styles.highlighted}`
                          : styles.tableCell
                      }
                      style={highlightStyle}
                    >
                      {Math.round(total.laps * trackLengthKm)}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TyreLapChart;
